import { Box, TableRow as MuiTableRow, styled } from "@mui/material";

import { FONT_SIZES } from "../../theme";
import { TableColumn } from "./types";

/** Body-row height for both flat and grouped tables. */
export const ROW_HEIGHT_PX = "60px";

/** Spacing applied to header cells. Header rows are slightly taller than body rows. */
export const HEADER_CELL_SX = { paddingTop: 2, paddingBottom: 2 } as const;

/** Common body-cell sx — no padding (the inner Box owns it), no internal border. */
export const BODY_CELL_SX = { p: 0, m: 0, whiteSpace: "nowrap" as const };

/** Width applied to leading cells so they collapse around their content (checkbox / chevron). */
export const LEADING_COL_WIDTH = "0%";

const PADDING_EDGE = 1;
const PADDING_STANDARD = 2;
const PADDING_CHECKBOX_PR = 0;
const PADDING_CHEVRON_PL_WITH_CHECKBOX = 0.5;
const PADDING_CHEVRON_PR = 1.5;
const PADDING_FIRST_DATA_PL_GROUPED = 0;

interface CellPosArgs {
    colIdx: number;
    /** Total visible cells in the row (data columns + leading cells). */
    totalColSpan: number;
    /** True in grouped mode (chevron column present). */
    isGrouped: boolean;
    /** Whether a leading checkbox cell is present (always at colIdx 0 when true). */
    hasCheckbox: boolean;
}

/**
 * Returns `{ paddingLeft, paddingRight }` for a cell. Encodes the alignment
 * rules across all four mode/selection combinations:
 *
 *   flat   no selection : data cells only (edge | standard | edge_right)
 *   flat   selection    : checkbox + data cells (checkbox.pr = 0; data.pl = standard)
 *   group  no selection : chevron + data cells (chevron.pl = edge, pr = 12px; first data.pl = 0)
 *   group  selection    : checkbox + chevron + data cells (chevron.pl = 4px because checkbox precedes it)
 */
export function getCellPadding({ colIdx, totalColSpan, isGrouped, hasCheckbox }: CellPosArgs) {
    const isLastCell = colIdx === totalColSpan - 1;
    const checkboxIdx = hasCheckbox ? 0 : -1;
    const chevronIdx = isGrouped ? (hasCheckbox ? 1 : 0) : -1;
    const firstDataIdx = (hasCheckbox ? 1 : 0) + (isGrouped ? 1 : 0);

    const isCheckboxCell = colIdx === checkboxIdx;
    const isChevronCell = colIdx === chevronIdx && isGrouped;
    const isFirstDataAfterChevron = colIdx === firstDataIdx && isGrouped;

    let paddingLeft: number;
    if (isChevronCell && hasCheckbox) paddingLeft = PADDING_CHEVRON_PL_WITH_CHECKBOX;
    else if (isChevronCell) paddingLeft = PADDING_EDGE;
    else if (isFirstDataAfterChevron) paddingLeft = PADDING_FIRST_DATA_PL_GROUPED;
    else if (colIdx === 0) paddingLeft = PADDING_EDGE;
    else paddingLeft = PADDING_STANDARD;

    let paddingRight: number;
    if (isLastCell) paddingRight = PADDING_EDGE;
    else if (isCheckboxCell) paddingRight = PADDING_CHECKBOX_PR;
    else if (isChevronCell) paddingRight = PADDING_CHEVRON_PR;
    else paddingRight = PADDING_STANDARD;

    return { paddingLeft, paddingRight };
}

/** Inner-Box sx for body cells — padding + vertical flex layout. */
export function getCellInnerSx(args: CellPosArgs) {
    return {
        ...getCellPadding(args),
        py: 0.5,
        fontSize: FONT_SIZES.body1,
        display: "flex",
        height: ROW_HEIGHT_PX,
        alignItems: "center"
    } as const;
}

export function colFlexAlign<T>(col: TableColumn<T>): "flex-start" | "flex-end" | "center" {
    if (col.align === "right") return "flex-end";
    if (col.align === "center") return "center";
    return "flex-start";
}

/**
 * One absolutely-positioned 1px line above the row. Mounts in a `position: relative`
 * parent. Single instance per row (replaces the previous design's N positioned slices
 * per row, which was 5–7× the DOM nodes for the same visual).
 */
export function RowDivider({ color }: { color: string }) {
    return (
        <Box
            sx={{
                position: "absolute",
                top: "-1px",
                left: 0,
                right: 0,
                height: "1px",
                backgroundColor: color
            }}
        />
    );
}

/** MUI `TableRow` with the internal cell border stripped. */
export const StyledTableRow = styled(MuiTableRow)(() => ({
    border: "none"
}));
